class ApellaBrowse extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.surface = this.querySelector('.apella-browse__surface');
    this.triggers = [...this.querySelectorAll('.apella-browse__trigger')];
    this.header = this.closest('.header');
    this.section = this.closest('.section-header');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.hover = matchMedia('(hover: hover) and (pointer: fine)');
    this.addEventListener('keydown', () => this.setAttribute('data-keyboard', ''), options);
    this.addEventListener('pointermove', () => this.removeAttribute('data-keyboard'), options);
    this.addEventListener('pointerdown', () => this.removeAttribute('data-keyboard'), options);
    this.triggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', () => {
        if (!this.hover.matches) return;
        this.cancelClose();
        clearTimeout(this.openTimer);
        this.moveIndicator(trigger);
        this.openTimer = setTimeout(() => trigger.dataset.panel ? this.open(trigger) : this.close(), 160);
      }, options);
      trigger.addEventListener('mouseleave', () => clearTimeout(this.openTimer), options);
      if (trigger.dataset.panel) {
        trigger.addEventListener('click', event => {
          this.toggleAttribute('data-keyboard', event.detail === 0);
          this.active === trigger ? this.close() : this.open(trigger);
        }, options);
        trigger.addEventListener('keydown', event => {
          if (event.key !== 'ArrowDown' && !(event.key === 'Tab' && !event.shiftKey && this.active === trigger)) return;
          event.preventDefault();
          this.setAttribute('data-keyboard', '');
          this.open(trigger);
          this.panel.querySelector('a,button')?.focus();
        }, options);
      }
    });
    this.addEventListener('mouseenter', () => {
      this.cancelClose();
      if (this.closingTrigger && !this.surface.hidden) this.open(this.closingTrigger);
    }, options);
    this.addEventListener('mouseleave', () => {
      clearTimeout(this.openTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 320);
    }, options);
    this.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (this.isConnected && !this.contains(document.activeElement) && !this.matches(':hover')) this.close();
      });
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' && event.target.closest('.apella-browse__details')) {
        event.preventDefault();
        const owner = this.panel.querySelector('[data-detail][aria-expanded="true"]');
        this.hideDetail(true);
        owner?.focus();
        return;
      }
      if (event.key !== 'Escape') return;
      event.preventDefault();
      const trigger = this.active;
      this.close();
      trigger?.focus();
    }, options);
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.close();
    }, options);
    this.querySelectorAll('[data-detail]').forEach(button => {
      button.addEventListener('mouseenter', () => {
        if (!this.hover.matches) return;
        clearTimeout(this.detailTimer);
        this.detailTimer = setTimeout(() => this.showDetail(button), 320);
      }, options);
      button.addEventListener('mouseleave', () => clearTimeout(this.detailTimer), options);
      button.addEventListener('click', event => {
        this.toggleAttribute('data-keyboard', event.detail === 0);
        button.getAttribute('aria-expanded') === 'true' ? this.hideDetail() : this.showDetail(button);
      }, options);
      button.addEventListener('keydown', event => {
        if (event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.setAttribute('data-keyboard', '');
        this.showDetail(button);
        this.querySelector(`[id="${button.getAttribute('aria-controls')}"]`)?.querySelector('a')?.focus();
      }, options);
    });
    this.querySelectorAll('.apella-browse__category:not([data-detail])').forEach(row => {
      row.addEventListener('mouseenter', () => {
        if (!this.hover.matches) return;
        clearTimeout(this.detailTimer);
        this.detailTimer = setTimeout(() => this.hideDetail(), 360);
      }, options);
      row.addEventListener('mouseleave', () => clearTimeout(this.detailTimer), options);
    });
    this.querySelectorAll('.apella-browse__details').forEach(detail => {
      detail.addEventListener('mouseenter', () => clearTimeout(this.detailTimer), options);
    });
    this.querySelectorAll('.apella-browse__categories').forEach(list => {
      list.addEventListener('scroll', () => this.hideDetail(true), { ...options, passive: true });
    });
    window.addEventListener('resize', () => {
      if (getComputedStyle(this).display === 'none') this.close(true);
      else if (this.active) { this.resizePanel(); this.moveIndicator(this.active); }
      else this.restoreIndicator();
    }, options);
    this.restoreIndicator();
    document.fonts?.ready.then(() => this.isConnected && this.restoreIndicator());
    this.sizeObserver = new ResizeObserver(() => this.active && this.resizePanel());
    this.querySelectorAll('.apella-browse__panel').forEach(panel => this.sizeObserver.observe(panel));
  }

  disconnectedCallback() {
    this.close(true);
    clearTimeout(this.detailHideTimer);
    this.events?.abort();
    this.sizeObserver?.disconnect();
    this.events = null;
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
    clearTimeout(this.hideTimer);
  }

  moveIndicator(trigger) {
    const label = trigger.querySelector('.apella-menu-label__text');
    if (!label) return;
    const rect = label.getBoundingClientRect();
    const origin = this.header.getBoundingClientRect();
    this.style.setProperty('--indicator-x', `${rect.left - origin.left}px`);
    this.style.setProperty('--indicator-y', `${rect.bottom - origin.top + 8}px`);
    this.style.setProperty('--indicator-width', `${rect.width}px`);
    this.setAttribute('data-indicator', '');
  }

  restoreIndicator() {
    const current = this.active || this.triggers.find(trigger => trigger.matches('[aria-current="page"]') || trigger.querySelector('.header__active-menu-item'));
    if (current) this.moveIndicator(current);
    else this.removeAttribute('data-indicator');
  }

  revealRows(container) {
    const rows = [...container.children].map(row => row.querySelector('a,button'));
    rows.forEach((row, index) => {
      row?.getAnimations().forEach(animation => animation.cancel());
      if (!row || this.motion.matches || this.hasAttribute('data-keyboard')) return;
      row.animate([
        { opacity: 0, transform: 'translateY(3px)' },
        { opacity: 1, transform: 'none' },
      ], { duration: 260, delay: Math.min(index * 18, 108), fill: 'backwards', easing: 'cubic-bezier(.22,1,.36,1)' });
    });
  }

  open(trigger) {
    this.cancelClose();
    clearTimeout(this.openTimer);
    clearTimeout(this.detailTimer);
    if (this.active === trigger) return;
    const previous = this.panel;
    this.closingTrigger = null;
    this.active?.setAttribute('aria-expanded', 'false');
    this.active = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    this.panel = this.querySelector(`[id="${trigger.dataset.panel}"]`);
    const resuming = previous === this.panel && !this.surface.hidden;
    // Cancel old exits before a rapid A -> B -> A switch can hide the current panel.
    this.panel.getAnimations().forEach(animation => animation.cancel());
    if (previous && previous !== this.panel) {
      previous.inert = true;
      previous.getAnimations().forEach(animation => animation.cancel());
      if (this.motion.matches || this.hasAttribute('data-keyboard')) previous.hidden = true;
      else {
        const outgoing = previous.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120 });
        outgoing.onfinish = () => { if (previous !== this.panel) previous.hidden = true; };
      }
    }
    this.panel.hidden = false;
    this.panel.inert = false;
    this.surface.hidden = false;
    if (!resuming) this.hideDetail(true);
    this.resizePanel();
    this.surface.getBoundingClientRect();
    this.setAttribute('data-open', '');
    // CSS reverses an interrupted exit from its current position. Replaying the
    // content reveal here would flash already-visible rows back to transparent.
    if (!resuming) this.revealRows(this.panel.querySelector('.apella-browse__categories'));
    this.moveIndicator(trigger);
    if (this.section) this.section.classList.add('menu-open');
  }

  resizePanel() {
    if (!this.panel) return;
    const header = this.header.getBoundingClientRect();
    const trigger = this.active?.getBoundingClientRect();
    if (!trigger) return;
    const width = Math.min(300, header.width - 32);
    this.surface.style.width = `${width}px`;
    this.surface.style.left = `${Math.max(16, Math.min(trigger.left - header.left - 12, header.width - width - 16))}px`;
    this.surface.style.transformOrigin = `${Math.max(0, Math.min(width, trigger.left + trigger.width / 2 - header.left - parseFloat(this.surface.style.left)))}px top`;
    this.style.setProperty('--browse-max-height', `${Math.max(100, window.innerHeight - header.bottom - 24)}px`);
    this.surface.style.height = `${this.panel.getBoundingClientRect().height}px`;
    const detailOwner = this.panel.querySelector('[data-detail][aria-expanded="true"]');
    if (detailOwner) this.positionDetail(detailOwner);
  }

  showDetail(button, animate = true) {
    if (!this.panel || !this.panel.contains(button)) return;
    const resuming = this.panel.hasAttribute('data-detail-closing') &&
      this.panel.querySelector('.apella-browse__detail:not([hidden])')?.id === button.dataset.detail;
    clearTimeout(this.detailHideTimer);
    this.panel.removeAttribute('data-detail-closing');
    const old = this.panel.querySelector('[data-detail][aria-expanded="true"]');
    if (old === button) return;
    clearTimeout(this.detailTimer);
    this.panel.querySelectorAll('[data-detail]').forEach(item => item.setAttribute('aria-expanded', String(item === button)));
    this.panel.querySelectorAll('.apella-browse__detail').forEach(detail => {
      detail.hidden = detail.id !== button.dataset.detail;
    });
    const detail = this.panel.querySelector('.apella-browse__detail:not([hidden])');
    this.panel.querySelector('.apella-browse__details').inert = false;
    this.panel.setAttribute('data-detail-open', '');
    this.positionDetail(button);
    if (animate && detail && !resuming) this.revealRows(detail.querySelector('ul'));
    this.resizePanel();
  }

  positionDetail(button) {
    const panel = this.panel.getBoundingClientRect();
    const details = this.panel.querySelector('.apella-browse__details');
    this.panel.dataset.detailSide = panel.right + details.offsetWidth > window.innerWidth - 16 ? 'left' : 'right';
    const availableY = window.innerHeight - panel.top - details.offsetHeight - 16;
    this.panel.style.setProperty('--detail-y', `${Math.max(0, Math.min(button.getBoundingClientRect().top - panel.top - 8, availableY))}px`);
  }

  hideDetail(immediate = false) {
    clearTimeout(this.detailTimer);
    clearTimeout(this.detailHideTimer);
    if (!this.panel) return;
    const panel = this.panel;
    const wasOpen = panel.hasAttribute('data-detail-open');
    panel.removeAttribute('data-detail-open');
    panel.querySelector('.apella-browse__details').inert = true;
    panel.querySelectorAll('[data-detail]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    const hide = () => {
      panel.removeAttribute('data-detail-closing');
      panel.querySelectorAll('.apella-browse__detail').forEach(detail => { detail.hidden = true; });
    };
    if (immediate || !wasOpen || this.motion.matches || this.hasAttribute('data-keyboard')) hide();
    else {
      panel.setAttribute('data-detail-closing', '');
      this.detailHideTimer = setTimeout(hide, 160);
    }
  }

  close(immediate = false) {
    this.cancelClose();
    clearTimeout(this.openTimer);
    clearTimeout(this.detailTimer);
    this.closingTrigger = this.active || this.closingTrigger;
    this.active?.setAttribute('aria-expanded', 'false');
    this.active = null;
    this.removeAttribute('data-open');
    this.section?.classList.remove('menu-open');
    if (this.panel) this.panel.inert = true;
    const hide = () => { this.surface.hidden = true; this.surface.style.height = '0px'; this.closingTrigger = null; };
    if (immediate || this.motion.matches || this.hasAttribute('data-keyboard')) hide();
    else this.hideTimer = setTimeout(hide, 300);
    this.restoreIndicator();
  }
}
if (!customElements.get('apella-browse')) customElements.define('apella-browse', ApellaBrowse);
